import { useEffect, useRef, useState } from "react";
import { Loader2, CheckCircle2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { invokeInternal } from "../invoke";
import type { StepProps } from "../types";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ClientAccountStep = ({ data, update }: StepProps) => {
  const [sending, setSending] = useState(false);
  const pollRef = useRef<number | null>(null);

  const stopPolling = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => stopPolling, []);

  const startPolling = (email: string) => {
    stopPolling();
    pollRef.current = window.setInterval(async () => {
      // Keep auth-account state behind the same fail-closed internal secret as
      // the rest of this wizard. The public database API no longer exposes an
      // email/account-readiness oracle.
      const { data: state, error } = await invokeInternal<{
        account_ready?: boolean;
      }>("internal-business-state", {
        clientEmail: email.toLowerCase().trim(),
      });
      if (error) return;
      if (state?.account_ready === true) {
        update({ accountCreated: true });
        stopPolling();
        toast.success("Client has set their password");
      }
    }, 4000);
  };

  // Pick the status back up after a reload or coming back from a later step.
  useEffect(() => {
    if (data.inviteSent && !data.accountCreated && data.clientEmail) {
      startPolling(data.clientEmail);
    }
    // Runs once on mount: sendLink starts polling for later changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendLink = async () => {
    const email = data.clientEmail.toLowerCase().trim();
    if (!emailRe.test(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    setSending(true);
    try {
      const { data: res, error } = await invokeInternal<{
        success?: boolean;
        error?: string;
        existing_account?: boolean;
        account_ready?: boolean;
        support_access?: string;
      }>("internal-client-invite", {
        email,
        origin: window.location.origin,
      });
      if (error) throw new Error(error.message);
      if (res?.error) throw new Error(res.error);
      if (res?.account_ready) {
        update({ accountCreated: true, inviteSent: true });
        toast.success(`${email} already has a Dibs account`);
        return;
      }
      update({ inviteSent: true });
      toast.success(`Setup link sent to ${email}`);
      if (res?.support_access === "failed") {
        toast.warning("Dibs support access could not be added. It will be tried again on the next step.");
      }
      startPolling(email);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to send setup link");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-heading font-semibold">
          Create the client's account
        </h2>
        <p className="text-sm text-muted-foreground">
          Enter the client's business email. We'll send them a link to set their
          password, and you can carry on without waiting for them.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="clientEmail">Client business email</Label>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            id="clientEmail"
            type="email"
            placeholder="owner@theircompany.com"
            value={data.clientEmail}
            disabled={data.accountCreated}
            onChange={(e) => {
              stopPolling();
              update({
                clientEmail: e.target.value,
                accountCreated: false,
                inviteSent: false,
              });
            }}
          />
          <Button onClick={sendLink} disabled={sending || data.accountCreated}>
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            {data.inviteSent ? "Resend link" : "Send setup link"}
          </Button>
        </div>
      </div>

      {data.inviteSent && !data.accountCreated && (
        <div className="rounded-lg border bg-secondary/50 p-4 text-sm">
          <p className="font-medium">Setup link sent</p>
          <p className="text-muted-foreground">
            You can continue now. The client can set their password whenever
            they get to it.
          </p>
        </div>
      )}

      {data.accountCreated && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
          <CheckCircle2 className="h-5 w-5 text-primary" />
          <p className="text-sm font-medium">
            This client can sign in. You can continue onboarding.
          </p>
        </div>
      )}
    </div>
  );
};

export default ClientAccountStep;
